import type { MatrixQuarterlyReport, Revision } from "epsm-api-types/models";

export interface ColumnValues {
	"0d"?: number;
	"2d"?: number;
	"3d"?: number;
	"4d"?: number;
	"7d"?: number;
	"30d"?: number;
	"60d"?: number;
	"90d"?: number;
	"180d"?: number;
}

export interface TableColumn {
	type: string;
	year: number;
	quarter?: number;
	values?: ColumnValues;
	changes?: ColumnValues;
	number_of_analysts?: number;
	dispersion?: number;
	std_dev_p?: number;
	mean?: number;
	std_dev?: number;
}

export interface Estimates {
	value: TableColumn[];
	scores_info?: ColumnValues;
}

export interface SourceInformation<T> {
	rs?: Estimates;
	rss?: Estimates;
	ee?: T;
	re?: Estimates;
}

export interface DataTypes<T> {
	fh?: T;
	ns?: T;
}

export interface MainTable<T = Estimates>
	extends DataTypes<SourceInformation<T>> {}

export type TrendType = "up" | "down" | "rev-up" | "rev-down";

export interface ExtraDates {
	earning_date?: string;
}

export interface EstimatesRevision {
	pct_chg_ed?: number;
	pct_chg_90d?: number;
	pct_chg_cq?: number;
	pct_chg_7d?: number;
	abs_chg_ed?: number;
	abs_chg_90d?: number;
	trend_ed?: TrendType;
	trend_90d?: TrendType;
	extra_info?: ExtraDates;
}

export interface RevisionExt extends Revision {
	ex?: MainTable<Estimates>;
	mt?: MainTable<Estimates>;
	dp?: MainTable<TableColumn[]>;
	rs?: DataTypes<number>;
	rss?: DataTypes<number>;
	rs_chg?: DataTypes<EstimatesRevision>;
	rss_chg?: DataTypes<EstimatesRevision>;
}

export enum MatrixReportField {
	EPS = "eps",
	SALES = "sales",
	PROFIT_MARGIN = "profit_margin",
	FREE_CASH_FLOW = "free_cash_flow",
	EPS_BNRI = "eps_bnri",
}

export type MatrixReportFields = Pick<MatrixQuarterlyReport, MatrixReportField>;

export enum RevisionsScoreChartDataSource {
	ZACKS = "ns",
	FINNHUB = "fh",
}

export enum AnalystsEstimatesChartTimeFrame {
	QUARTERS = "Q",
	ANNUALS = "A",
}

export enum AnalystsEstimatesChartViewMode {
	DISPERSION = "dispersion",
	STD_DEVIATION = "std_dev",
}

export enum RevisionDataViewMode {
	EPS_RS = "ee",
	SALES_RS = "re",
}

export enum RevisionDataValueMode {
	VALUES = "values",
	CHANGES = "changes",
}

export enum RevisionDataSource {
	ZACKS = "ns",
	OTHER = "fh",
}

export interface RevisionState {
	ticker?: string;
	color?: string;
	earningsMatrixViewMode?: MatrixReportField;
	revisionsScoreChartDataSource?: RevisionsScoreChartDataSource;
	analystsEstimatesChartTimeFrame?: AnalystsEstimatesChartTimeFrame;
	analystsEstimatesChartViewMode?: AnalystsEstimatesChartViewMode;
	revisionDataViewMode?: RevisionDataViewMode;
	revisionDataValueMode?: RevisionDataValueMode;
	revisionDataSource?: RevisionDataSource;
}
